import React, { useState, useEffect, useCallback } from 'react';
import {
  IconPlus,
  IconSearch,
  IconCalendar,
  IconGrip,
} from './Icons';
import { supabase } from '../lib/supabase';
import './PurchaseOrders.css';

export default function PurchaseOrders() {
  const [tab, setTab] = useState('monthly'); // monthly | yearly | custom
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [projects, setProjects] = useState([]);

  const fetchProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, title')
        .order('title', { ascending: true, nullsFirst: false });
      setProjects(error ? [] : (data ?? []));
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const labelPalette = [
    '#22c55e',
    '#10b981',
    '#0ea5e9',
    '#38bdf8',
    '#6b7280',
    '#e5e7eb',
    '#facc15',
    '#fb923c',
    '#f97316',
    '#ef4444',
    '#ec4899',
    '#6366f1',
    '#111827',
    '#a855f7',
  ];

  const [selectedColor, setSelectedColor] = useState(labelPalette[0]);
  const [labelName, setLabelName] = useState('');
  const [labels, setLabels] = useState([]);

  const COLUMNS = [
    { key: 'purchaseId', label: 'Purchase ID' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'purchaseType', label: 'Purchase Type' },
    { key: 'project', label: 'Project' },
    { key: 'billDate', label: 'Bill date' },
    { key: 'dueDate', label: 'Due date' },
    { key: 'totalInvoiced', label: 'Total invoiced' },
    { key: 'paymentReceived', label: 'Payment Received' },
    { key: 'due', label: 'Due' },
    { key: 'status', label: 'Status' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    COLUMNS.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: true,
      }),
      {},
    ),
  );

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const columns = COLUMNS.filter((col) => visibleColumns[col.key] !== false);
  const activeColumnCount = columns.length + 1; // +1 for actions column

  const renderPeriodLabel = () => {
    if (tab === 'yearly') return '2026';
    if (tab === 'custom') return null;
    return 'February 2026';
  };

  return (
    <div className="po-page">
      <div className="po-card">
        <div className="po-header-row">
          <div className="po-title-group">
            <h1 className="po-title">Purchase Orders</h1>
            <div className="po-tabs">
              <button
                type="button"
                className={`po-tab ${tab === 'monthly' ? 'active' : ''}`}
                onClick={() => setTab('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`po-tab ${tab === 'yearly' ? 'active' : ''}`}
                onClick={() => setTab('yearly')}
              >
                Yearly
              </button>
              <button
                type="button"
                className={`po-tab ${tab === 'custom' ? 'active' : ''}`}
                onClick={() => setTab('custom')}
              >
                Custom
              </button>
            </div>
          </div>

          <div className="po-header-actions">
            <button
              type="button"
              className="po-header-btn"
              onClick={() => setShowLabelsModal(true)}
            >
              Manage labels
            </button>
            <button
              type="button"
              className="po-header-btn"
              onClick={() => setShowPaymentModal(true)}
            >
              Add payment
            </button>
            <button
              type="button"
              className="po-header-btn po-header-btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <IconPlus />
              Add Purchase Orders
            </button>
          </div>
        </div>

        <div className="po-filters-row">
          <div className="po-filters-left">
            <div className="po-column-toggle-wrap">
              <button
                type="button"
                className="po-select-toggle po-column-toggle"
                aria-label="Choose visible columns"
                onClick={() => setColumnMenuOpen((open) => !open)}
              >
                <span className="po-column-dots">●●●</span>
              </button>

              {columnMenuOpen && (
                <div className="po-column-menu">
                  {COLUMNS.map((col) => (
                    <button
                      key={col.key}
                      type="button"
                      className={`po-column-menu-item${
                        visibleColumns[col.key] !== false ? ' active' : ''
                      }`}
                      onClick={() => toggleColumn(col.key)}
                    >
                      <span>{col.label}</span>
                      {visibleColumns[col.key] !== false && (
                        <span className="po-column-check">✓</span>
                      )}
                    </button>
                  ))}
                  <div className="po-column-menu-divider" />
                  <div className="po-column-menu-footer">
                    <IconGrip />
                  </div>
                </div>
              )}
            </div>

            <div className="po-select">- Type -</div>
            <div className="po-select">- Status -</div>
            <div className="po-select">- Purchase Type -</div>
            <div className="po-select">- Supplier -</div>
            <div className="po-select">- Currency -</div>

            {tab !== 'custom' && (
              <div className="po-date-picker">
                <button
                  type="button"
                  className="po-date-btn"
                  aria-label="Previous period"
                >
                  ‹
                </button>
                <div className="po-date-display">{renderPeriodLabel()}</div>
                <button
                  type="button"
                  className="po-date-btn"
                  aria-label="Next period"
                >
                  ›
                </button>
              </div>
            )}

            {tab === 'custom' && (
              <div className="po-custom-range">
                <input
                  className="po-custom-input"
                  defaultValue="27th February 2026"
                />
                <input
                  className="po-custom-input"
                  defaultValue="27th February 2026"
                />
              </div>
            )}
          </div>

          <div className="po-filters-right">
            <button type="button" className="po-utility-btn">
              Excel
            </button>
            <div className="po-search">
              <input type="text" placeholder="Search" aria-label="Search purchases" />
              <span className="po-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        <div className="po-table-wrapper">
          <table className="po-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="po-table-empty" colSpan={activeColumnCount}>
                  No record found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="po-footer-row">
          <div className="po-footer-left">
            <select
              className="po-page-size"
              defaultValue="10"
              aria-label="Rows per page"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>0-0 / 0</span>
          </div>
          <div className="po-footer-right">
            <button
              type="button"
              className="po-page-btn"
              aria-label="Previous page"
            >
              «
            </button>
            <button
              type="button"
              className="po-page-btn"
              aria-label="Next page"
            >
              »
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add Purchase Orders</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                <div className="estimates-form-label">Bill date</div>
                <input
                  className="estimates-form-input"
                  defaultValue="2026-02-27"
                />

                <div className="estimates-form-label">Due date</div>
                <input
                  className="estimates-form-input"
                  defaultValue="2026-03-13"
                />

                <div className="estimates-form-label">Supplier</div>
                <select className="estimates-form-select" defaultValue="">
                  <option value="">-</option>
                </select>

                <div className="estimates-form-label">Project</div>
                <select className="estimates-form-select" defaultValue="" aria-label="Project">
                  <option value="">—</option>
                  {(projects || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title || 'Untitled'}
                    </option>
                  ))}
                </select>

                <div className="estimates-form-label">Type</div>
                <select className="estimates-form-select" defaultValue="Order">
                  <option value="Order">Order</option>
                </select>

                <div className="estimates-form-label">TAX</div>
                <select className="estimates-form-select" defaultValue="">
                  <option value="">-</option>
                </select>

                <div className="estimates-form-label">VAT</div>
                <input
                  type="text"
                  className="estimates-form-input"
                  placeholder="—"
                  defaultValue=""
                />

                <div className="estimates-form-label">
                  Recurring
                  <span className="po-recurring-hint">?</span>
                </div>
                <div>
                  <label className="po-recurring-checkbox">
                    <input type="checkbox" />
                  </label>
                </div>

                <div className="estimates-form-label">Note</div>
                <textarea className="estimates-form-textarea" />

                <div className="estimates-form-label">Labels</div>
                <input className="estimates-form-input" placeholder="Labels" />
              </div>

            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowAddModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={() => setShowAddModal(false)}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add payment</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowPaymentModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                <div className="estimates-form-label">Purchase</div>
                <select className="estimates-form-select" defaultValue="">
                  <option value="">-</option>
                </select>

                <div className="estimates-form-label">Payment method</div>
                <select className="estimates-form-select" defaultValue="Bank Transfer">
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>

                <div className="estimates-form-label">Payment date</div>
                <input
                  className="estimates-form-input"
                  defaultValue="2026-02-27"
                />

                <div className="estimates-form-label">Amount</div>
                <input className="estimates-form-input" placeholder="Amount" />

                <div className="estimates-form-label">Note</div>
                <textarea
                  className="estimates-form-textarea"
                  defaultValue="Description"
                />
              </div>
            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowPaymentModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={() => setShowPaymentModal(false)}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showLabelsModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => setShowLabelsModal(false)}
        >
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Manage labels</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowLabelsModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="estimates-modal-body">
              <div className="po-label-color-row">
                {labelPalette.map((color) => (
                  <div
                    key={color}
                    className={`po-label-color-swatch${
                      selectedColor === color ? ' selected' : ''
                    }`}
                    style={{ background: color }}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
              <div
                className="estimates-form-grid"
                style={{ gridTemplateColumns: '1fr auto' }}
              >
                <input
                  className="estimates-form-input"
                  placeholder="Label"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                />
                <button
                  type="button"
                  className="estimates-footer-btn estimates-footer-btn-primary"
                  onClick={() => {
                    if (!labelName.trim()) return;
                    setLabels((prev) => [
                      ...prev,
                      { name: labelName.trim(), color: selectedColor },
                    ]);
                    setLabelName('');
                  }}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowLabelsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

